import { memo } from "react";
import { Platform, Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { Text } from "@src/components/Text";
import Logo from "@src/assets/svgs/logo-variant-red.svg";
import type { FC } from "react";

type TabsHeaderProps = {
  selectedSiteName: string | null;
  onHamburgerMenuPress: () => void;
  onClearSite?: () => void;
  showLogo?: boolean;
  title?: string;
  headerShadowVisible?: boolean;
  showHamburgerMenu?: boolean;
};

export const TabsHeader: FC<TabsHeaderProps> = memo(
  ({
    selectedSiteName,
    onHamburgerMenuPress,
    onClearSite,
    showLogo = true,
    title,
    headerShadowVisible = false,
    showHamburgerMenu = true,
  }) => {
    const { theme } = useUnistyles();

    return (
      <View style={[styles.headerView, headerShadowVisible ? styles.headerShadow : undefined]}>
        <View style={styles.topRow}>
          <View flexDirection="row" alignItems="center">
            {showLogo && <Logo width={124} height={32} color={theme.currentTheme === "dark" ? "#FFFFFF" : "#24255c"} />}
            {title && <Text textStyle="md/medium">{title}</Text>}
          </View>
          {showHamburgerMenu && (
            <Pressable onPress={onHamburgerMenuPress} style={styles.hamburgerMenuButton}>
              <Icon name="hamburgerMenu" size={20} color={theme.colors.text} />
            </Pressable>
          )}
        </View>
        <View flexDirection="row" alignItems="center" gap="xs">
          <Icon name="building" size={20} color={theme.colors.text} />
          <Text textStyle="sm/regular" numberOfLines={1} style={styles.siteName}>
            {selectedSiteName ?? "All sites"}
          </Text>
          {selectedSiteName && onClearSite && (
            <Pressable onPress={onClearSite} accessibilityRole="button" accessibilityLabel="Clear site filter">
              <Icon name="closeCircle" size={20} color={theme.colors.textMuted} />
            </Pressable>
          )}
        </View>
      </View>
    );
  },
);

TabsHeader.displayName = "TabsHeader";

const styles = StyleSheet.create((theme) => ({
  headerView: {
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing["5xl"],
    paddingBottom: theme.spacing["xl"],
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  siteName: {
    flexShrink: 1,
    minWidth: 0,
  },
  hamburgerMenuButton: {
    borderColor: theme.colors.interactive.buttons.default_background,
    borderWidth: 1,
    borderRadius: theme.spacing.md,
    height: 36,
    width: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  headerShadow: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.border,
    ...Platform.select({
      android: {
        backgroundColor: theme.colors.background,
      },
      ios: {
        shadowOpacity: 0.3,
        shadowRadius: 0,
        shadowOffset: { width: 0, height: StyleSheet.hairlineWidth },
        shadowColor: theme.currentTheme === "dark" ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 1)",
      },
    }),
  },
}));
